import { ERN_MAP_KEY_PATTERN, releaseTypes, type PartnerRole, type ReleaseType } from '@eis/core';
import type { z } from 'zod';

/**
 * The publish manifest as a form, and back again.
 *
 * A manifest is four maps of `name → ERN` plus three scalars (service summary §5.1). Rendering
 * each map as a repeating key/value widget was the first idea and the wrong one: a manifest is
 * edited by copying the previous version and changing one or two lines, and a row editor makes
 * that the slowest possible interaction. So each map is one text field, one `key = ern` per
 * line — the shape the manifest already has in review and in a commit message.
 *
 * Nothing here validates. Parsing produces a document; `tarifVersionManifestSchema` (in
 * @eis/core, kept honest against api/schemas/manifest by tools/contracts/manifest-parity)
 * decides whether that document is a manifest. Splitting the two is what lets a malformed LINE
 * ("no equals sign") be reported separately from a malformed VALUE ("not an ERN"): the first is
 * a typing mistake in this editor, the second is a claim about the platform.
 */

export interface ManifestFormValues {
  readonly description: string;
  readonly releaseType: ReleaseType;
  /** Empty means "no baseline" and is sent as an explicit null — see the schema's `basedOn`. */
  readonly basedOn: string;
  readonly refs: string;
  readonly documents: string;
  readonly workflows: string;
  readonly settings: string;
  /**
   * The partner roles a policy on this version may carry (SDD-011 P-1, P-2). STRUCTURED, not a
   * `key = ern` text: a role has a cardinality, and a text line cannot carry one honestly. The
   * form copies the list from the previous version like the other blocks; the editor that changes
   * it is the "Partnerrollen" block (prompt 17 phase 5).
   */
  readonly partnerRoles: readonly PartnerRole[];
}

/** The role every list must carry, exactly once, with minCount 1 and maxCount 1 (SDD-011 P-6). */
export const POLICYHOLDER_ROLE_ERN = 'partnerservice:tag/policyholder';

/**
 * What a version starts with when there is no previous version to copy from: the policyholder
 * alone. The gate would refuse anything less (P-6), so the form never offers less.
 */
export function defaultPartnerRoles(): PartnerRole[] {
  return [
    {
      roleErn: POLICYHOLDER_ROLE_ERN,
      name: 'Policyholder',
      isRequired: true,
      isMulti: false,
      minCount: 1,
      maxCount: 1,
    },
  ];
}

/** The four map-shaped blocks, in the order the form and the manifest table both show them. */
export const manifestBlocks = ['refs', 'documents', 'workflows', 'settings'] as const;
export type ManifestBlock = (typeof manifestBlocks)[number];

export interface ManifestIssue {
  /** Which field the message belongs under; `manifest` when it belongs to none. */
  readonly field: keyof ManifestFormValues | 'manifest';
  /** The offending key inside a block, when there is one. */
  readonly at?: string;
  readonly messageKey: string;
}

export interface ParsedBlock {
  readonly entries: Record<string, string>;
  /** Lines that are not `key = value` at all — reported before the schema ever sees them. */
  readonly malformed: readonly string[];
}

export function parseErnMap(text: string): ParsedBlock {
  const entries: Record<string, string> = {};
  const malformed: string[] = [];

  for (const raw of text.split('\n')) {
    const line = raw.trim();
    // A blank line separates blocks while editing; a `#` line is how a reviewer leaves a note in
    // the paste they were sent. Neither is data.
    if (line === '' || line.startsWith('#')) continue;

    const separator = line.indexOf('=');
    if (separator === -1) {
      malformed.push(line);
      continue;
    }
    const key = line.slice(0, separator).trim();
    const value = line.slice(separator + 1).trim();
    if (key === '' || value === '') {
      malformed.push(line);
      continue;
    }
    entries[key] = value;
  }

  return { entries, malformed };
}

export function formatErnMap(map: Readonly<Record<string, string>> | undefined): string {
  if (map === undefined) return '';
  return Object.keys(map)
    .sort((a, b) => a.localeCompare(b))
    .map((key) => `${key} = ${String(map[key])}`)
    .join('\n');
}

/**
 * The document the form describes — the exact bytes the publish request will carry.
 *
 * `basedOn` is emitted as `null` rather than omitted when empty: the gate refuses a PATCH with no
 * baseline (there is nothing to prove the premium did not move against), and a manifest that
 * simply left the key out would be making a weaker claim than the operator made.
 */
export function toManifestDocument(values: ManifestFormValues): Record<string, unknown> {
  return {
    description: values.description.trim(),
    releaseType: values.releaseType,
    basedOn: values.basedOn.trim() === '' ? null : values.basedOn.trim(),
    refs: parseErnMap(values.refs).entries,
    documents: parseErnMap(values.documents).entries,
    workflows: parseErnMap(values.workflows).entries,
    settings: parseErnMap(values.settings).entries,
    partnerRoles: values.partnerRoles,
  };
}

/** Custom messages the schema sets are already codes; anything else is mapped from the issue. */
const SCHEMA_CODES = new Set(['ern', 'key', 'required', 'unique']);

function messageKeyOf(issue: z.core.$ZodIssue): string {
  if (SCHEMA_CODES.has(issue.message)) return `tarifs.publish.errors.${issue.message}`;
  switch (issue.code) {
    case 'unrecognized_keys':
      return 'tarifs.publish.errors.unknownField';
    case 'invalid_value':
      return 'tarifs.publish.errors.releaseType';
    // A record whose KEY is malformed. Zod nests the key's own issue inside this one, so the
    // sentinel the schema set is not on the outer message — the code is what identifies it.
    case 'invalid_key':
      return 'tarifs.publish.errors.key';
    // A partner role's bound outside what the schema allows — `maxCount: 0`, `minCount: -1`.
    case 'too_small':
    case 'too_big':
      return 'tarifs.publish.errors.range';
    default:
      return 'tarifs.publish.errors.missing';
  }
}

function fieldOf(issue: z.core.$ZodIssue): { field: ManifestIssue['field']; at?: string } {
  const [head, ...rest] = issue.path;
  const key = typeof head === 'string' ? head : '';
  const at = rest.filter((segment) => typeof segment === 'string').join('.');

  switch (key) {
    case 'description':
    case 'releaseType':
    case 'basedOn':
    case 'refs':
    case 'documents':
    case 'workflows':
    case 'settings':
      return at === '' ? { field: key } : { field: key, at };
    case 'partnerRoles': {
      // A role issue is addressed by row and attribute — `0.maxCount` — because the block is a
      // list, not a map, and the operator needs to find the row.
      const pointer = rest.map(String).join('.');
      return pointer === '' ? { field: key } : { field: key, at: pointer };
    }
    default:
      return { field: 'manifest' };
  }
}

/**
 * Everything wrong with the form, as a flat list — malformed lines first, then schema issues.
 *
 * An empty array means the manifest is one the service will accept as far as SHAPE goes. It says
 * nothing about the gate: whether the refs resolve and whether the premium moved are questions
 * only the service can answer, and pretending otherwise here is exactly how a console starts
 * telling operators things it cannot know.
 */
export function validateManifestForm(
  values: ManifestFormValues,
  /**
   * The schema deciding what a manifest IS — `tarifVersionManifestSchema` or its group
   * counterpart. Passed in rather than chosen here: the two differ only in `refs`, and the
   * editor has no business knowing which layer it is editing.
   */
  schema: { safeParse: (value: unknown) => z.ZodSafeParseResult<unknown> },
): ManifestIssue[] {
  const issues: ManifestIssue[] = [];

  for (const block of manifestBlocks) {
    for (const line of parseErnMap(values[block]).malformed) {
      issues.push({ field: block, at: line, messageKey: 'tarifs.publish.errors.line' });
    }
  }

  const parsed = schema.safeParse(toManifestDocument(values));
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      issues.push({ ...fieldOf(issue), messageKey: messageKeyOf(issue) });
    }
  }
  return issues;
}

/** Prefills the form from a published version's manifest — editing beats retyping. */
export function manifestFormFrom(
  manifest: Readonly<Record<string, unknown>> | undefined,
  fallbackReleaseType: ReleaseType = 'PATCH',
): ManifestFormValues {
  const block = (name: ManifestBlock): string => {
    const value: unknown = manifest?.[name];
    if (typeof value !== 'object' || value === null) return '';
    return formatErnMap(value as Record<string, string>);
  };
  const releaseType: unknown = manifest?.releaseType;
  // Copied as it stands — the schema, not this function, decides whether every row is a rule. A
  // version published before roles existed has none; it starts from the policyholder alone.
  const roles: unknown = manifest?.partnerRoles;

  return {
    description: '',
    releaseType: isReleaseType(releaseType) ? releaseType : fallbackReleaseType,
    basedOn: typeof manifest?.basedOn === 'string' ? manifest.basedOn : '',
    refs: block('refs'),
    documents: block('documents'),
    workflows: block('workflows'),
    settings: block('settings'),
    partnerRoles:
      Array.isArray(roles) && roles.length > 0 ? (roles as PartnerRole[]) : defaultPartnerRoles(),
  };
}

export function isReleaseType(value: unknown): value is ReleaseType {
  return typeof value === 'string' && (releaseTypes as readonly string[]).includes(value);
}

/** Exported for the block editors' inline hints; the schema owns the rule. */
export const isValidMapKey = (key: string): boolean => ERN_MAP_KEY_PATTERN.test(key);
